// ─── CP (Casing Pressure) Workflow Models (M006) ─────────────────────────────

// ─── Stage constants ─────────────────────────────────────────────────────────

/** Stage values for the CP workflow pipeline (matches CpStage.cs on the API). */
export const CpStage = {
  InitialReading:    'InitialReading',
  Verification:      'Verification',
  Mitigation:        'Mitigation',
  Approval:          'Approval',
  Resolved:          'Resolved Completed',
  ResolvedError:     'Resolved-Error',
  ResolvedDivest:    'Resolved-Divest',
  ResolvedNotNeeded: 'Resolved-NotNeeded',
} as const;

export type CpStageValue = (typeof CpStage)[keyof typeof CpStage];

/** Cancellation reason options for the Cancel Case dialog. */
export const CpCancellationReason = {
  DuplicateHumanError: 'Duplicate/Human Error',
  WellDivested:        'Well Divested',
  NotNeeded:           'Not Needed',
} as const;

export type CpCancellationReasonValue = (typeof CpCancellationReason)[keyof typeof CpCancellationReason];

/** Payload for POST /api/cp-cases/{id}/cancel */
export interface CpCancelCaseRequest {
  cancellationReason: CpCancellationReasonValue;
  comments: string | null;
}

// ─── List / Page ─────────────────────────────────────────────────────────────

import type { ConservationStatus } from './rl-workflow';

export interface CpCaseListQuery {
  stage?: string;
  keyword?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface CpCaseListItem {
  id: number;
  caseNumber: string;
  wellName: string | null;
  fot: string | null;
  stage: string;
  createdDate: string;
  createdBy: string | null;
}

export interface CpCasePage {
  totalCount: number;
  items: CpCaseListItem[];
}

// ─── Child row models ─────────────────────────────────────────────────────────

export interface CpVerificationRow {
  id: number;
  readingType: string | null;
  datePressureRecorded: string | null;
  bradenPressure: number | null;
  intermediatePressure: number | null;
  productionPressure: number | null;
  wellStatus: string | null;
  comment: string | null;
  time: string | null;
  sampleCollected: boolean;
}

export interface SaveCpVerificationRowRequest {
  readingType?: string | null;
  datePressureRecorded?: string | null;
  bradenPressure: number | null;
  intermediatePressure: number | null;
  productionPressure: number | null;
  wellStatus?: string | null;
  comment?: string | null;
  time?: string | null;
  sampleCollected: boolean;
}

export interface CpMitigationRow {
  id: number;
  productionEngineerEntraOid: string | null;
  productionEngineerDisplayName: string | null;
  date: string | null;
  dueDate: string | null;
  action: string | null;
  responsiblePersonEntraOid: string | null;
  responsiblePersonDisplayName: string | null;
  status: string | null;
  comment: string | null;
}

export interface SaveCpMitigationRowRequest {
  productionEngineerEntraOid?: string | null;
  productionEngineerDisplayName?: string | null;
  date?: string | null;
  dueDate?: string | null;
  action?: string | null;
  responsiblePersonEntraOid?: string | null;
  responsiblePersonDisplayName?: string | null;
  status?: string | null;
  comment?: string | null;
}

export interface CpApprovalRow {
  id: number;
  nameEntraOid: string | null;
  nameDisplayName: string | null;
  role: string | null;
  approvalDate: string | null;
  approvalComments: string | null;
}

export interface SaveCpApprovalRowRequest {
  nameEntraOid?: string | null;
  nameDisplayName?: string | null;
  role?: string | null;
  approvalDate?: string | null;
  approvalComments?: string | null;
}

// ─── Case detail ─────────────────────────────────────────────────────────────

export interface CpCaseDetail {
  id: number;
  caseId: number;
  caseNumber: string;
  stage: string;
  reOpenCount: number;

  // Well Information (read-only after creation — BRU-011)
  wellName: string | null;
  api: string | null;
  state: string | null;
  area: string | null;
  fot: string | null;
  sectionName: string | null;
  chevno: string | null;
  gaugerBeatCode: string | null;
  costCenter: string | null;
  wellTest1: number | null;
  wellTest2: number | null;
  wellTest3: number | null;

  // Initial Reading (inline on CpCase — ADR-M6-002)
  readingType: string | null;
  datePressureRecorded: string | null;
  bradenPressure: number | null;
  intermediatePressure: number | null;
  productionPressure: number | null;
  wellStatus: string | null;
  hcpRiskRanking: string | null;
  scpRiskRanking: string | null;
  comment: string | null;

  // Child rows
  verifications: CpVerificationRow[];
  mitigations: CpMitigationRow[];
  approvals: CpApprovalRow[];

  // Approver feedback
  approverFeedback: string | null;

  // Metadata
  createdDate: string;
  createdBy: string | null;
  updatedDate: string;
  updatedBy: string | null;

  // Conservation / protected-species status
  conservationStatus: ConservationStatus | null;
}

// ─── Mutation requests ────────────────────────────────────────────────────────

export interface CreateCpCaseRequest {
  caseId: number;
}

export interface SaveCpCaseRequest {
  readingType?: string | null;
  datePressureRecorded?: string | null;
  bradenPressure?: number | null;
  intermediatePressure?: number | null;
  productionPressure?: number | null;
  wellStatus?: string | null;
  hcpRiskRanking?: string | null;
  scpRiskRanking?: string | null;
  comment?: string | null;
  approverFeedback?: string | null;
}

// ─── Move validation ─────────────────────────────────────────────────────────

export interface CpMoveValidationError {
  field: string;
  message: string;
}

// ─── Audit log ───────────────────────────────────────────────────────────────

/** @deprecated Use AuditLogEntry from '#models' instead. */
export type CpAuditLogEntry = import('./audit-log').AuditLogEntry;

// ─── Attachments ────────────────────────────────────────────────────────────────────

export interface CpAttachment {
  id: number;
  caseId: number;
  attachmentType: string;
  fileName: string;
  uploadedBy?: string | null;
  uploadedDate: string;
  fileSizeBytes: number;
}

export interface CpCorrespondenceItem {
  id: number;
  subject: string;
  notification: string;
  recipients: string;
  sentOnUtc: string;
  deliveryStatus: string;
  hasDownload: boolean;
}
